import { NgModule } from '@angular/core';
import { RedirectFunction, RouterModule, Routes, UrlSegmentGroup, UrlTree } from '@angular/router';

import { AcademyComponent } from '@academy/academy.component';
import { AuthGuard } from '@core/guard/auth-guard/auth.guard';
import { DefaultPageGuard } from '@core/guard/default-page-guard/default-page.guard';
import { hasPermission, hasUserCondition } from '@core/guard/permission-guard/permission.guard';

// The login route path, extracted so it can be tested for consistency with AuthService's
// createLoginUrl() (see auth.service.ts) without needing to load the actual router config.
export const LOGIN_ROUTE_PATH = 'login';

// Extracted so this transformation of an old /mobile/<path> URL into /<path> can be unit tested
// without needing to load the actual router config.
export const redirectMobilePath: RedirectFunction = ({ url, queryParams, fragment }) => {
  return new UrlTree(
    new UrlSegmentGroup([], { primary: new UrlSegmentGroup(url.slice(1), {}) }),
    queryParams,
    fragment,
  );
};

const routes: Routes = [
  {
    path: LOGIN_ROUTE_PATH,
    loadChildren: () => import('./features/signin/signin.module').then((m) => m.SigninModule),
    title: '',
  },
  {
    path: '',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'configuration',
        loadChildren: () =>
          import('./features/configuration/configuration.module').then(
            (m) => m.ConfigurationModule,
          ),
        title: 'config.TITLE',
        pathMatch: 'full',
      },
      {
        path: 'orders',
        canActivate: [
          hasPermission(['REPORTING', 'CALCULATIONS', 'ORDERS', 'DEFERRED_SAMPLES', 'BILLS']),
        ],
        loadChildren: () =>
          import('./features/your-labs/your-labs.module').then((m) => m.YourLabsModule),
        title: 'Your Labs',
      },
      {
        path: 'evaluation/toetsing',
        canActivate: [hasPermission('CALCULATIONS')],
        loadChildren: () =>
          import('./features/evaluation/toetsing/toetsing-evaluation.module').then(
            (m) => m.ToetsingEvaluationModule,
          ),
        title: 'calcs.TITLE',
      },
      {
        path: 'logistics',
        canActivate: [hasPermission('LOGISTICS')],
        loadChildren: () =>
          import('./features/logistics/logistics.module').then((m) => m.LogisticsModule),
        title: 'logistics.TITLE',
      },
      {
        path: 'results',
        canActivate: [hasPermission('REPORTING')],
        loadChildren: () =>
          import('./features/results/results.module').then((m) => m.ResultsModule),
        title: 'results.TITLE',
      },
      {
        path: 'upload',
        canActivate: [hasPermission('UPLOAD')],
        loadChildren: () => import('./features/upload/upload.module').then((m) => m.UploadModule),
      },
      {
        path: 'team',
        canActivate: [hasUserCondition((user) => !!user.hasSubUsers)],
        loadChildren: () =>
          import('./features/authorisations/authorisations.module').then(
            (m) => m.AuthorisationsModule,
          ),
        title: 'authorisations.title',
      },
      {
        path: 'academy',
        component: AcademyComponent,
        title: 'academy.title',
      },
      {
        path: 'prices',
        canActivate: [hasUserCondition((user) => user.mayUsePriceList)],
        loadChildren: () => import('./features/prices/prices.module').then((m) => m.PricesModule),
        title: 'prices.TITLE_NAV',
      },
      {
        path: 'order',
        canActivate: [hasPermission('ORDERS')],
        loadChildren: () =>
          import('./features/ordering/ordering.module').then((m) => m.OrderingModule),
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/dashboard/dashboard.module').then((m) => m.DashboardModule),
        title: 'home.TITLE',
        pathMatch: 'full',
      },
      {
        // The guard will check which is the startpage of the user (default)
        path: 'default',
        canActivate: [DefaultPageGuard],
        children: [],
      },
    ],
  },
  {
    path: 'mobile',
    redirectTo: redirectMobilePath,
  },
  {
    path: '**',
    redirectTo: '/default',
    pathMatch: 'full',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { useHash: true })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
